// Components==============
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faCamera } from '@fortawesome/pro-solid-svg-icons';
import Avatar from 'components/UI/Avatar/Avatar';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import Input from 'components/UI/Form/Input';
import Textarea from 'components/UI/Form/Textarea';
import { EditableProcessEntity } from 'hooks/useProcessGroups';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext, useRef, useState } from 'react';
import { ProjectProcessContext } from '../../ProjectProcess';
import './StartPoint.module.scss';
// =========================

export default function Edit({ process }: { process?: EditableProcessEntity }) {
  const { processGroups } = useContext(ProjectProcessContext);

  const { setProcessGroups, project } = useContext(ProjectDetailsContext);

  const translate = useTranslate();

  const description = processGroups?.find(
    (group) =>
      group.id === process?.id && group.process_type === process.process_type
  )?.description as StartPointType | undefined;

  const initialStakeholder: StakeholderEntity = {
    id: 1,
    role: '',
    file: undefined,
    image: '',
    name: '',
  };
  const [stakeholderState, setStakeholderState] = useState(initialStakeholder);
  const [toggleFocus, setToggleFocus] = useState(-1);

  const labelRef = useRef<HTMLLabelElement>(null);

  const stateChangeHelper = (newVal: StartPointType) =>
    setProcessGroups((prev) =>
      prev.map((group) => {
        if (
          group.id === process?.id &&
          group.process_type === process.process_type
        ) {
          return {
            ...group,
            description: {
              ...(group.description as StartPointType),
              ...newVal,
            },
          };
        }
        return group;
      })
    );

  function onChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    // limit design_question chars
    if (e.target.value.length >= 150 && e.target.name === 'design_question')
      return;

    stateChangeHelper({
      [e.target.name]: e.target.value,
    });
  }

  function handleAddSubQuestion(value: string) {
    const newId = description?.sub_questions?.length
      ? description?.sub_questions?.[description?.sub_questions.length - 1]
          ?.id + 1
      : 1;

    if (!value) return;

    setToggleFocus(newId);

    if (description?.sub_questions?.length)
      return stateChangeHelper({
        sub_questions: [...description?.sub_questions, { id: newId, value }],
      });

    stateChangeHelper({
      sub_questions: [{ id: newId, value }],
    });
  }

  function handleDeleteQuestion(questionId: number) {
    return stateChangeHelper({
      sub_questions: description?.sub_questions?.filter(
        (q) => q.id !== questionId
      ),
    });
  }

  function handleSubQuestionChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    questionId: number
  ) {
    if (!e.target.value) {
      setToggleFocus(0);

      return stateChangeHelper({
        sub_questions: description?.sub_questions?.filter(
          (q) => q.id !== questionId
        ),
      });
    }

    stateChangeHelper({
      sub_questions: description?.sub_questions?.map((q) =>
        q.id === questionId ? { ...q, value: e.target.value } : q
      ),
    });
  }

  function handleMemberRole(
    e: React.ChangeEvent<HTMLInputElement>,
    member_id: number
  ) {
    if (!description?.member_roles?.length)
      return stateChangeHelper({
        member_roles: [{ member_id, role: e.target.value }],
      });

    if (
      description?.member_roles?.find(
        (role) => role.member_id === member_id
      ) === undefined
    )
      return stateChangeHelper({
        member_roles: [
          ...description?.member_roles,
          { member_id, role: e.target.value },
        ],
      });

    stateChangeHelper({
      member_roles: description?.member_roles?.map((role) =>
        role.member_id === member_id ? { ...role, role: e.target.value } : role
      ),
    });
  }

  function handleAddStakeholder(e?: React.KeyboardEvent<HTMLInputElement>) {
    if (
      (e?.key === undefined || e?.key === 'Enter') &&
      stakeholderState.image &&
      stakeholderState.name &&
      stakeholderState.role
    ) {
      const newId = description?.stakeholders
        ? description?.stakeholders?.[description?.stakeholders.length - 1]
            ?.id + 1
        : NaN;

      const id = newId || 1;

      setStakeholderState(initialStakeholder);
      if (description?.stakeholders?.length)
        return stateChangeHelper({
          stakeholders: [
            ...description?.stakeholders,
            { ...stakeholderState, id },
          ],
        });

      stateChangeHelper({
        stakeholders: [{ ...stakeholderState, id }],
      });
    }
  }

  function uploadStakeholderImage(
    e: React.ChangeEvent<HTMLInputElement>,
    id?: number
  ) {
    const file = e.target.files?.[0]!;
    if (file) {
      if (id) {
        stateChangeHelper({
          stakeholders: description?.stakeholders?.map((s) =>
            s.id === id ? { ...s, file } : s
          ),
        });
      } else {
        setStakeholderState((prev) => ({ ...prev, file }));
      }
    }

    const imageReader = new FileReader();

    imageReader.addEventListener('load', () => {
      if (typeof imageReader.result === 'string') {
        if (id) {
          stateChangeHelper({
            stakeholders: description?.stakeholders?.map((s) =>
              s.id === id ? { ...s, image: imageReader.result as string } : s
            ),
          });
        } else {
          setStakeholderState((prev) => ({
            ...prev,
            image: imageReader.result as string,
          }));
        }
      }
    });

    if (file) imageReader.readAsDataURL(file);
  }

  function handleDeleteStakeholder(id: number) {
    stateChangeHelper({
      stakeholders: description?.stakeholders?.filter((s) => s.id !== id),
    });
  }

  function handleStakeholderChange(
    e: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) {
    stateChangeHelper({
      stakeholders: description?.stakeholders?.map((s) =>
        s.id === id ? { ...s, [e.target.name]: e.target.value } : s
      ),
    });
  }

  const members = project?.members.length
    ? [
        ...project?.members
          .filter(
            (member) =>
              member.membership !== 'invited' && member.membership !== 'teacher'
          )
          .map((member) => member.user),
      ]
    : [];

  return (
    <div styleName="grid">
      <div>
        <h3>
          {translate('project.terms.design-question')} <span>*</span>
        </h3>
        <Textarea
          ext_formValue={description?.design_question}
          ext_onChange={onChange}
          name="design_question"
          placeholder={translate('project.inputs.design-question')}
        />
      </div>
      <div>
        <h3>{translate('project.inputs.project-description')}</h3>
        <Textarea
          ext_formValue={description?.content}
          ext_onChange={onChange}
          name="content"
          placeholder={translate('project.inputs.project-description')}
        />
      </div>
      <div>
        <h3>{translate('project.terms.sub-question')}</h3>
        <div styleName="sub-grid">
          {description?.sub_questions?.map((q, i) => (
            <Input
              ext_formValue={q.value}
              ext_onChange={(e) => handleSubQuestionChange(e, q.id)}
              onKeyDown={(e) => e.key === 'Enter' && setToggleFocus(0)}
              name="sub_question"
              placeholder={translate('project.terms.describe-sub-question')}
              handleDelete={() => handleDeleteQuestion(q.id)}
              key={i}
              focusEffect={q.id === toggleFocus}
            />
          ))}
          <div styleName="new">
            <Input
              name="new_sub_question"
              ext_onChange={(e) => {
                if (e.target.value) handleAddSubQuestion(e.target.value);
              }}
              placeholder={translate('project.terms.add-sub-question')}
              focusEffect={toggleFocus === 0}
            />
          </div>
        </div>
      </div>
      {!!members?.length && (
        <div>
          <div styleName="roles-grid">
            <h3>Team</h3>
            <h3>{translate('general.terms.role')}</h3>
          </div>
          <div styleName="sub-grid">
            {members.map((member, i) => {
              if (!member) return <></>;
              const role = description?.member_roles?.find(
                (r) => r.member_id === member.id
              );

              return (
                <div styleName="roles-grid" key={i}>
                  <div styleName="user">
                    <Avatar user={member} />
                    <p>
                      {member.first_name} {member.last_name}
                    </p>
                  </div>
                  <Input
                    ext_formValue={role?.role || ''}
                    ext_onChange={(e) => handleMemberRole(e, member.id)}
                    name="member_role"
                    placeholder={translate('general.terms.role')}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <div styleName="roles-grid">
          <h3>Stakeholders & experts</h3>
          <h3>{translate('general.terms.role')}</h3>
        </div>
        <div styleName="sub-grid">
          {description?.stakeholders?.map((stakeholder) => (
            <div styleName="roles-grid" key={stakeholder.id}>
              <div styleName="user">
                <label htmlFor={`btn-${stakeholder.id}`}>
                  <img src={stakeholder.image} alt={stakeholder.name} />
                </label>
                <Input
                  ext_formValue={stakeholder.name}
                  ext_onChange={(e) =>
                    handleStakeholderChange(e, stakeholder.id)
                  }
                  name="name"
                  placeholder={translate('general.terms.who')}
                />
              </div>
              <Input
                ext_formValue={stakeholder.role}
                ext_onChange={(e) => handleStakeholderChange(e, stakeholder.id)}
                name="role"
                placeholder={translate('general.terms.role')}
                handleDelete={() => handleDeleteStakeholder(stakeholder.id)}
              />
              <input
                type="file"
                id={`btn-${stakeholder.id}`}
                onChange={(e) => uploadStakeholderImage(e, stakeholder.id)}
                name="upload"
              />
            </div>
          ))}
          <div styleName="roles-grid">
            <div styleName="user">
              {stakeholderState.image ? (
                <label htmlFor="btn">
                  <img
                    src={stakeholderState.image}
                    alt={stakeholderState.name}
                  />
                </label>
              ) : (
                <>
                  <label htmlFor="btn" ref={labelRef}>
                    <CircleButton
                      icon={faCamera}
                      color="dark-grey"
                      onClick={() =>
                        labelRef.current ? labelRef.current.click() : undefined
                      }
                    />
                  </label>
                </>
              )}
              <input
                type="file"
                id="btn"
                onChange={(e) => uploadStakeholderImage(e)}
                name="upload"
                accept=".jpg,.jpeg,.png"
              />
              <Input
                ext_formValue={stakeholderState.name}
                ext_onChange={(e) =>
                  setStakeholderState((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                name="name"
                placeholder="Expert"
                onKeyDown={handleAddStakeholder}
              />
            </div>
            <div styleName="input-button">
              <Input
                ext_formValue={stakeholderState.role}
                ext_onChange={(e) =>
                  setStakeholderState((prev) => ({
                    ...prev,
                    role: e.target.value,
                  }))
                }
                name="role"
                placeholder={translate('general.terms.role')}
                onKeyDown={handleAddStakeholder}
              />
              {!!(
                stakeholderState.image &&
                stakeholderState.name &&
                stakeholderState.role
              ) && (
                <CircleButton
                  icon={faPlus}
                  color="grey"
                  onClick={() => handleAddStakeholder()}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
